import { useMemo } from "react";
import {
  commitStepsFromFiles,
  mergeWorkflowSteps,
} from "../../shared/mergeCore";
import { queries } from "../../shared/queries";
import { hasConflictMarkers } from "../../shared/threeWayMerge";
import { useQuery } from "./useQuery";

export type MainMergeStatus = "loading" | "ready" | "conflicts" | "unresolved";

/**
 * Replays the merge a push live would run, from the same synced rows the
 * mutator reads; "unresolved" means the head itself still carries conflict
 * markers from an earlier merge.
 */
export function useMainMergeStatus(
  branchId: string,
  mainBranchId: string | undefined
): MainMergeStatus {
  const [branch, branchDetails] = useQuery(
    queries.loadBranchSummary({ branchId })
  );
  const [main, mainDetails] = useQuery(
    queries.loadBranchSummary({ branchId: mainBranchId ?? "" })
  );
  const isDraft = branch != null && branch.name !== "main";
  const [headFiles, headDetails] = useQuery(
    queries.loadAllCommitFiles({
      commitId: isDraft && branch.headCommitId ? branch.headCommitId : "",
    })
  );
  const behind =
    isDraft &&
    branch.forkCommitId != null &&
    branch.headCommitId != null &&
    main?.headCommitId != null &&
    branch.forkCommitId !== main.headCommitId;
  const [baseFiles, baseDetails] = useQuery(
    queries.loadAllCommitFiles({
      commitId: behind ? branch.forkCommitId! : "",
    })
  );
  const [mainFiles, mainFilesDetails] = useQuery(
    queries.loadAllCommitFiles({
      commitId: behind ? main!.headCommitId! : "",
    })
  );

  return useMemo(() => {
    if (branchDetails.type !== "complete" || mainDetails.type !== "complete") {
      return "loading";
    }
    if (!isDraft) return "ready";
    if (headDetails.type !== "complete") return "loading";
    if (
      headFiles.some(
        (f) => f.blob != null && hasConflictMarkers(f.blob.content)
      )
    ) {
      return "unresolved";
    }
    if (!behind) return "ready";
    if (
      baseDetails.type !== "complete" ||
      mainFilesDetails.type !== "complete"
    ) {
      return "loading";
    }
    const result = mergeWorkflowSteps(
      commitStepsFromFiles(baseFiles),
      commitStepsFromFiles(mainFiles),
      commitStepsFromFiles(headFiles)
    );
    return result.fileConflicts.length > 0 || result.deleteConflicts.length > 0
      ? "conflicts"
      : "ready";
  }, [
    isDraft,
    behind,
    branchDetails.type,
    mainDetails.type,
    headDetails.type,
    baseDetails.type,
    mainFilesDetails.type,
    headFiles,
    baseFiles,
    mainFiles,
  ]);
}
